/**
 * Keeping queued sales on disk — and knowing when the browser is about to take
 * that disk away.
 *
 * Unsynced sales live in IndexedDB. Without persistent storage the browser may
 * evict the whole origin under pressure, which for a shop means a day of takings
 * disappears with no error anywhere. Persistence is not guaranteed (an
 * uninstalled site is often refused), so the quota is also checked: a nearly
 * full profile gets a visible warning instead of a silent loss.
 */

const isBrowser = typeof window !== 'undefined'

/** Above this share of the quota, the queue is worth warning about. */
const QUOTA_WARNING_RATIO = 0.8

let persistenceRequested = false

/**
 * Ask once per page load. Called when the first sale is queued, not at startup:
 * the request is more likely to be granted for a site the user is actively
 * using, and a shop that never sells offline never pays for it.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!isBrowser || persistenceRequested) return false
  persistenceRequested = true

  try {
    if (typeof navigator.storage?.persisted === 'function' && (await navigator.storage.persisted())) {
      return true
    }
    if (typeof navigator.storage?.persist === 'function') {
      return await navigator.storage.persist()
    }
  } catch {
    // Private mode or a browser that refuses: the queue still works this
    // session, it is just more likely to be evicted later.
  }
  return false
}

/** True when the origin is using enough of its quota that eviction is a risk. */
export async function isStorageAtRisk(): Promise<boolean> {
  if (!isBrowser) return false
  try {
    const estimate = await navigator.storage?.estimate?.()
    if (!estimate?.quota || estimate.quota <= 0) return false
    return (estimate.usage ?? 0) / estimate.quota > QUOTA_WARNING_RATIO
  } catch {
    return false
  }
}

/** Test seam: forget that persistence was already asked for. */
export function resetPersistenceRequestForTests(): void {
  persistenceRequested = false
}
